import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import QaRolesCustomSelect from 'components/QaRolesCustomSelect';
import useOptions from 'router/subrouters/DataRoom/subrouters/Members/hooks/useOptions';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import MemberRoles from '../../../MemberRoles';
import ResolveMultiSelect from 'components/ResolveMultiSelect';
import MemberFileAccessSelector, {
  MemberFileAccessSelectorRef,
} from '../../../MemberFileAccessSelector/MemberFileAccessSelector.component';
import ReleaseOrderNote from '../../../MemberFileAccessSelector/components/ReleaseOrderNote';
import ResolvePopup from 'components/ResolvePopup';
import LinkToIcon from 'icons/LinkTo.icon';
import {
  getDataRoomRoute,
  getIsArrayEmpty,
  getResolveUrl,
  getSellingPortfolio,
} from 'utils';
import TrashIcon from 'icons/Trash.icon';
import NoEntryIcon from 'icons/NoEntry.icon';
import useDeleteMember from 'router/subrouters/DataRoom/hooks/useDeleteMember';
import {
  findDataRoomMember,
  isDataRoomAdminOrOwner,
} from 'router/subrouters/DataRoom/utils';
import KeyActions from '../../../KeyActions';
import useCallbackRef from 'hooks/useCallbackRef';

import './MemberSettings.styles.scss';

type MemberSettingsProps = {
  className?: string;
  member: DataRoomMember;
  isBuySide: boolean;
  drawerOpen: boolean;
  defaultOpenRoles?: boolean;
  hasAdditionalActions?: boolean;
  refetchFlag: boolean;
  hasFileAccess: boolean;
  isLoadingRevoke: boolean;
  isUndoAvailable: boolean;
  onRevokeAccess: () => void;
  refetch: () => void;
  updateMember?: (member: DataRoomMember) => void;
  updateDataRoomMembers?: (members: DataRoomMembers) => void;
  closeDrawer: () => void;
};

const MemberSettings: React.FC<MemberSettingsProps> = (props) => {
  const {
    className,
    member,
    isBuySide,
    drawerOpen,
    defaultOpenRoles = false,
    hasAdditionalActions,
    refetchFlag,
    hasFileAccess,
    isLoadingRevoke,
    isUndoAvailable,
    onRevokeAccess,
    refetch,
    updateMember,
    updateDataRoomMembers,
    closeDrawer,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  const [fileAccessSelector, fileAccessSelectorRef] =
    useCallbackRef<MemberFileAccessSelectorRef>(null);

  const classes = classNames('member-settings', className);

  const { buyerRoleOptions, buyerQaRole, sellerRoleOptions, sellerQaRole } =
    useOptions();

  const {
    dataRoom: { _id: dataRoomId, teams, dealRoomId, dealRoom },
  } = useContext(DataRoomContext);

  const { popServerError } = useContext(PopNotificationsContext);

  const isAdmin = isDataRoomAdminOrOwner(member?.role);

  const { t } = useTranslation();

  const buyerStep = member?.lead?.buyerStep;

  const releaseOrderNote = useMemo(
    () => (
      <ReleaseOrderNote
        name={member?.firstName}
        automationLink={
          // The Data Room is served on its own origin, so the link leaves it. Trans fills in the text.
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a
            href={getResolveUrl(
              `${getSellingPortfolio(dealRoomId, 'settings')}${
                buyerStep ? `/${buyerStep}` : ''
              }`,
            )}
          />
        }
      />
    ),
    [member?.firstName, buyerStep, dealRoomId],
  );

  const dataRoomTeamsOptions = useMemo(() => {
    const array = Array.from(
      new Set([...(teams || []), ...(member?.teams || [])]),
    );

    return array.map((team) => ({
      value: team,
      label: team,
    }));
  }, [teams, member]);

  const onSubmit = useCallback(
    async (values: Partial<DataRoomMember>) => {
      try {
        setIsLoading(true);
        const { data } = await dataRoomApi.members.updateMember(
          member?._id,
          values,
        );

        const updatedMember = {
          ...member,
          ...findDataRoomMember(data, member._id),
        };

        updateMember?.(updatedMember);
        fileAccessSelector?.updateNodeIds(updatedMember);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [member, fileAccessSelector, popServerError, updateMember],
  );

  const hasNoTeamRoles = getIsArrayEmpty(dataRoomTeamsOptions);

  const teamRolesSelect = (
    <ResolveMultiSelect
      options={dataRoomTeamsOptions}
      value={member?.teams || []}
      disabled={hasNoTeamRoles}
      renderAsPortal
      portalClassName="documents-multi-select"
      onChange={(teams) => onSubmit({ teams })}
    />
  );

  const deleteMember = useDeleteMember();

  const handleRemoveMember = useCallback(
    async () =>
      deleteMember({
        member,
        setIsLoading,
        onSuccess: (data) => {
          updateDataRoomMembers?.(data);
          closeDrawer();
        },
      }),
    [closeDrawer, deleteMember, member, updateDataRoomMembers],
  );

  return (
    <div className={classes}>
      {drawerOpen && !isAdmin && (
        <MemberFileAccessSelector
          ref={fileAccessSelectorRef}
          dataRoomId={dataRoomId}
          refetchFlag={refetchFlag}
          isDisabled={isUndoAvailable}
          dataRoomMemberId={member?._id}
          leadStagesCopies={dealRoom?.leadStagesCopies}
          releaseOrderNote={releaseOrderNote}
          refetch={refetch}
          updateMember={updateMember}
        />
      )}

      <MemberRoles defaultOpen={defaultOpenRoles}>
        <QaRolesCustomSelect
          name="role"
          label={t('filesRole')}
          options={isBuySide ? buyerRoleOptions : sellerRoleOptions}
          renderAsPortal
          value={member?.role}
          onChange={onSubmit}
        />

        <QaRolesCustomSelect
          name="qaRole"
          label={t('qaRole')}
          options={isBuySide ? buyerQaRole : sellerQaRole}
          renderAsPortal
          value={member?.qaRole}
          onChange={onSubmit}
        />

        <div className="column">
          <label>{t('assignTeamRole')}</label>

          {hasNoTeamRoles ? (
            <ResolvePopup
              onHover
              popupContent={t('noTeamRolesDefinedYet')}
              className="team-roles-popup"
              preferUpwards
            >
              {teamRolesSelect}
            </ResolvePopup>
          ) : (
            teamRolesSelect
          )}
        </div>

        <span className="manage-in-settings">
          {t('manageTeamRoles')}
          <Link to={getDataRoomRoute(dataRoomId, 'settings')}>
            {t('inYourSettings')}
            <LinkToIcon />
          </Link>
        </span>

        {isLoading && <Loader />}
      </MemberRoles>

      {hasAdditionalActions && !isAdmin && (
        <KeyActions description={t('keyActionsDescription')}>
          {hasFileAccess && (
            <Button
              variant="outline"
              icon={NoEntryIcon}
              disabled={isLoadingRevoke}
              onClick={onRevokeAccess}
            >
              {t('revokeAllFileAccess')}
            </Button>
          )}

          <Button variant="solid" icon={TrashIcon} onClick={handleRemoveMember}>
            {t('deleteMember')}
          </Button>
        </KeyActions>
      )}
    </div>
  );
};

export default MemberSettings;
